/**
 * Metadata model editor. Edits a local draft (autosaved in this browser),
 * shows a live diff against the published version, and publishes through a
 * server-computed review that lists exactly which stored values migrate or
 * move to the archive.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, EyeOff, FolderPlus, History, Plus, RotateCcw, Send, Trash2, X, FileStack } from 'lucide-react';
import {
  diffSchemas, metadataTabs, sectionTabId,
  type FieldDefinition, type MetadataSchemaDefinition,
} from '@prism/contracts';
import { api, errorMessage, type ApiError } from '../../api/client';
import { useAdminModel, useMe } from '../../api/hooks';
import { queryKeys } from '../../api/queryKeys';
import { Modal } from '../../components/Modal';
import { useSearchParams } from 'react-router-dom';
import { StrataAdmin } from './StrataAdmin';
import { CategoriesAdmin } from './CategoriesAdmin';
import { GroupTypesAdmin } from '../groups/GroupTypesAdmin';
import { MasterVariantsAdmin } from './MasterVariantsAdmin';
import { toast } from '../../components/Toasts';
import { relative } from '../../lib/format';
import { safeStorage } from '../../lib/prefs';
import * as ops from './model/modelDraftOps';
import { useModelDnd } from './model/useModelDnd';
import { SectionCard } from './model/SectionCard';
import { ChangeList, PublishReview, type Review } from './model/PublishReview';
import { FieldInspector } from './model/FieldInspector';
import { AddFieldDialog } from './model/AddFieldDialog';

interface Draft {
  baseVersion: number;
  definition: MetadataSchemaDefinition;
}

function draftKey(ws: string) {
  return `prism.modelDraft.${ws}`;
}

export function ModelEditor() {
  const me = useMe().data!;
  const q = useAdminModel();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [ack, setAck] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [params, setParams] = useSearchParams();
  // Fields, Asset classes, Master variants (1.2.0) or Timecode layers (?view=classes|variants|layers; the older ?layers=1 still opens the layers).
  // 1.4.0: Group types (?view=group-types, G2's GroupTypesAdmin).
  const view: 'fields' | 'classes' | 'variants' | 'layers' | 'group-types' = params.get('view') === 'classes' ? 'classes' : params.get('view') === 'variants' ? 'variants'
    : params.get('view') === 'group-types' ? 'group-types' : params.get('view') === 'layers' || params.get('layers') === '1' ? 'layers' : 'fields';
  const [removing, setRemoving] = useState<FieldDefinition | null>(null);
  const [showChanges, setShowChanges] = useState(false);
  /** The section the Add field dialog adds to, while it is open. */
  const [addingTo, setAddingTo] = useState<string | null>(null);
  /** Apply an edit to a fresh copy of the draft. */
  const update = (fn: (d: MetadataSchemaDefinition) => void) => {
    if (!draft) return;
    const next = structuredClone(draft.definition);
    fn(next);
    setDraft({ ...draft, definition: next });
  };
  const dnd = useModelDnd(update);

  // Restore an in-progress draft for this base version, or start from the published model.
  useEffect(() => {
    if (!q.data || draft) return;
    let restored: Draft | null = null;
    try {
      const raw = safeStorage.getItem(draftKey(me.workspace.id));
      restored = raw ? (JSON.parse(raw) as Draft) : null;
    } catch {
      restored = null;
    }
    setDraft(restored && restored.baseVersion === q.data.current.version ? restored : { baseVersion: q.data.current.version, definition: structuredClone(q.data.current.definition) });
  }, [q.data, draft, me.workspace.id]);

  useEffect(() => {
    if (!draft) return;
    safeStorage.setItem(draftKey(me.workspace.id), JSON.stringify(draft));
  }, [draft, me.workspace.id]);

  const published = q.data?.current.definition;
  const changes = useMemo(() => (published && draft ? diffSchemas(published, draft.definition) : []), [published, draft]);
  const changedFields = new Set(changes.map((c) => c.fieldId).filter(Boolean));

  const tabs = (
    <div className="segmented" role="tablist" aria-label="Model">
      <button role="tab" aria-pressed={view === 'fields'} aria-selected={view === 'fields'} onClick={() => setParams({})} data-testid="model-fields-tab">Fields</button>
      <button role="tab" aria-pressed={view === 'classes'} aria-selected={view === 'classes'} onClick={() => setParams({ view: 'classes' })} data-testid="model-classes-tab">Asset classes</button>
      <button role="tab" aria-pressed={view === 'variants'} aria-selected={view === 'variants'} onClick={() => setParams({ view: 'variants' })} data-testid="model-variants-tab">Master variants</button>
      <button role="tab" aria-pressed={view === 'layers'} aria-selected={view === 'layers'} onClick={() => setParams({ view: 'layers' })} data-testid="model-layers-tab">Timecode layers</button>
      <button role="tab" aria-pressed={view === 'group-types'} aria-selected={view === 'group-types'} onClick={() => setParams({ view: 'group-types' })} data-testid="model-group-types-tab">Group types</button>
    </div>
  );
  if (view === 'group-types') return <div className="page">{tabs}<div style={{ height: 16 }} /><GroupTypesAdmin /></div>;
  if (view === 'layers') return <div className="page">{tabs}<div style={{ height: 16 }} /><StrataAdmin embedded /></div>;
  if (view === 'variants') return <div className="page">{tabs}<div style={{ height: 16 }} /><MasterVariantsAdmin /></div>;
  if (view === 'classes') return <div className="page">{tabs}<div style={{ height: 16 }} /><CategoriesAdmin embedded /></div>;
  if (!q.data || !draft) return <div className="page"><div className="skeleton" style={{ height: 320 }} /></div>;
  const def = draft.definition;
  const field = def.fields.find((f) => f.id === selected) ?? null;
  const isNew = (id: string) => !published!.fields.some((f) => f.id === id);

  const patchField = (id: string, patch: Partial<FieldDefinition>) => update((d) => ops.patchField(d, id, patch));
  // Add field asks for the label and the permanent id first (fixes after rc.9 testing).
  const addField = (sectionId: string) => setAddingTo(sectionId);
  const createField = (label: string, id: string) => {
    const sectionId = addingTo!;
    update((d) => ops.addField(d, id, sectionId, label));
    setAddingTo(null);
    setSelected(id);
    setShowChanges(false);
  };
  const modelTabs = metadataTabs(def);

  async function openReview() {
    setAck(false);
    try {
      const res = await api<Review>('/api/v1/admin/metadata-schema/plan', {
        method: 'POST', json: { baseVersion: draft!.baseVersion, definition: def },
      });
      setReview(res);
    } catch (err) {
      const e = err as ApiError;
      toast(e.code === 'BAD_REQUEST' ? 'Some fields are incomplete. Check labels and options.' : e.message, 'error');
    }
  }

  async function publish() {
    setPublishing(true);
    try {
      const res = await api<{ version: number; migratedAssets: number }>('/api/v1/admin/metadata-schema/publish', {
        method: 'POST', json: { baseVersion: draft!.baseVersion, definition: def, acknowledgeArchiving: ack },
      });
      safeStorage.removeItem(draftKey(me.workspace.id));
      setReview(null);
      // Load the new published version before starting a fresh draft from it.
      await Promise.all([qc.invalidateQueries({ queryKey: queryKeys.adminModel() }), qc.invalidateQueries({ queryKey: queryKeys.schema() }), qc.invalidateQueries({ queryKey: queryKeys.assets() })]);
      setDraft(null);
      toast(`Published version ${res.version}${res.migratedAssets ? ` · ${res.migratedAssets} assets migrated` : ''}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setPublishing(false);
    }
  }

  const removedFields = published!.fields.filter((f) => !def.fields.some((x) => x.id === f.id));
  // One column per tab; without tabs, a single column holds every section.
  const columns: Array<{ id: string | null; label: string; index: number }> = modelTabs.length
    ? modelTabs.map((t, index) => ({ id: t.id, label: t.label, index }))
    : [{ id: null, label: 'Sections', index: 0 }];
  const sectionsOf = (tabId: string | null) => def.sections.filter((s) => (tabId ? sectionTabId(def, s) === tabId : true));
  const drawerOpen = Boolean(field) || showChanges;

  return (
    <div className="model-editor">
      <div className="model-toolbar">
        {tabs}
        <div>
          <strong>Asset metadata model</strong>
          <span className="muted"> · version {q.data.current.version}, published {relative(q.data.current.publishedAt)}</span>
        </div>
        <span style={{ flex: 1 }} />
        {changes.length > 0
          ? <button className="pill accent pill-button" data-testid="draft-changes" aria-pressed={showChanges} title="List the unpublished changes"
              onClick={() => { setShowChanges(!showChanges); setSelected(null); }}>{changes.length} unpublished change{changes.length > 1 ? 's' : ''}</button>
          : <span className="faint" style={{ fontSize: 12 }}>No changes</span>}
        <button className="btn ghost sm" onClick={() => setShowHistory(true)}><History size={14} /> History</button>
        <button className="btn ghost sm" disabled={!changes.length} onClick={() => { setDraft({ baseVersion: q.data!.current.version, definition: structuredClone(published!) }); setSelected(null); setShowChanges(false); }}><RotateCcw size={14} /> Discard</button>
        <button className="btn primary sm" disabled={!changes.length} onClick={() => void openReview()} data-testid="review-publish"><Send size={14} /> Review & publish</button>
      </div>
      <div className={`model-body ${drawerOpen ? 'with-drawer' : ''}`}>
        <div className="model-board" data-testid="model-board" aria-label="Metadata model"
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          {columns.map((col) => (
            <div key={col.id ?? 'all'} className={`model-column ${dnd.drop?.kind === 'section-end' && dnd.drop.tabId === col.id ? 'drop-into' : ''}`} data-tab={col.id ?? ''} data-testid="model-column"
              onDragOver={(e) => dnd.over(e, 'section', () => ({ kind: 'section-end', tabId: col.id }))}
              onDrop={dnd.performDrop}>
              <div className="model-column-head" data-testid="model-tabs">
                {col.id ? (
                  <>
                    <FileStack size={14} color="var(--muted)" />
                    <input className="input" value={col.label} aria-label="Tab name" data-testid="tab-name"
                      onChange={(e) => update((d) => { d.tabs!.find((x) => x.id === col.id)!.label = e.target.value; })} />
                    <button className="btn ghost icon sm" aria-label="Move tab left" disabled={col.index === 0} onClick={() => update((d) => ops.moveTab(d, col.id!, -1))}><ArrowLeft size={13} /></button>
                    <button className="btn ghost icon sm" aria-label="Move tab right" disabled={col.index === modelTabs.length - 1} onClick={() => update((d) => ops.moveTab(d, col.id!, 1))}><ArrowRight size={13} /></button>
                    <button className="btn ghost icon sm" aria-label="Remove tab" disabled={ops.tabInUse(def, col.id)} title={ops.tabInUse(def, col.id) ? 'Move its sections to another tab first' : 'Remove this tab'} onClick={() => update((d) => ops.removeTab(d, col.id!))}><X size={13} /></button>
                  </>
                ) : (
                  <>
                    <FileStack size={14} color="var(--muted)" />
                    <strong style={{ fontSize: 13 }}>{col.label}</strong>
                    <span className="faint" style={{ fontSize: 12, flex: 1 }}>No tabs: all sections show together</span>
                  </>
                )}
              </div>
              <div className="model-column-body">
                {sectionsOf(col.id).map((s) => (
                  <SectionCard key={s.id} s={s} def={def} dnd={dnd} selected={selected} onSelect={(id) => { setSelected(id); setShowChanges(false); }} onAddField={addField}
                    update={update} isNew={isNew} changed={changedFields} usage={q.data.usage} />
                ))}
                <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => update((d) => ops.addSection(d, col.id))} data-testid="add-section"><FolderPlus size={14} /> Section</button>
              </div>
            </div>
          ))}
          <div className="model-column model-column-add">
            <button className="btn ghost sm" onClick={() => update((d) => ops.addTab(d))} data-testid="add-tab"><Plus size={13} /> Tab</button>
            {!modelTabs.length && <span className="faint" style={{ fontSize: 12 }}>Group sections into tabs on the asset’s Metadata view</span>}
          </div>
          {removedFields.length > 0 && (
            <div className="model-column model-column-removed">
              <div className="model-column-head"><strong style={{ fontSize: 13 }}>Removed</strong></div>
              <div className="model-column-body">
                {removedFields.map((f) => (
                  <div key={f.id} className="model-field removed">
                    <span className="model-field-main"><span className="model-field-label">{f.label}</span><span className="model-field-meta">Removed · values will be archived</span></span>
                    <button className="btn ghost sm" onClick={() => update((d) => d.fields.push(structuredClone(f)))}>Restore</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {drawerOpen && (
          <aside className="model-drawer" aria-label={field ? 'Field settings' : 'Unpublished changes'} data-testid="model-drawer">
            <div className="model-drawer-head">
              <strong>{field ? (field.label || 'Unnamed field') : 'Unpublished changes'}</strong>
              <span style={{ flex: 1 }} />
              <button className="btn ghost icon sm" aria-label="Close panel" data-testid="close-drawer" onClick={() => { setSelected(null); setShowChanges(false); }}><X size={15} /></button>
            </div>
            <div className="model-drawer-body">
              {field ? (
                <FieldInspector
                  key={field.id}
                  field={field}
                  def={def}
                  isNew={isNew(field.id)}
                  published={published!.fields.find((f) => f.id === field.id) ?? null}
                  onChange={(p) => patchField(field.id, p)}
                  onMove={(dir) => update((d) => ops.moveField(d, field.id, dir))}
                  onRemove={() => {
                    if (q.data!.usage[field.id] && !isNew(field.id)) { setRemoving(field); return; }
                    update((d) => ops.removeField(d, field.id));
                    setSelected(null);
                  }}
                />
              ) : (
                <div style={{ padding: 20 }}><ChangeList changes={changes} /></div>
              )}
            </div>
          </aside>
        )}
      </div>

      {review && (
        <PublishReview version={q.data.current.version + 1} review={review} ack={ack} onAck={setAck} publishing={publishing} onPublish={() => void publish()} onClose={() => setReview(null)} />
      )}
      {addingTo && <AddFieldDialog def={def} published={published!} onCreate={createField} onClose={() => setAddingTo(null)} />}
      {removing && (
        <Modal title={`Remove “${removing.label}”?`} onClose={() => setRemoving(null)} actions={<>
          <button className="btn ghost" onClick={() => setRemoving(null)}>Cancel</button>
          <button className="btn" onClick={() => { patchField(removing.id, ops.HIDE_FIELD); setRemoving(null); toast('Hidden; publish to apply'); }}><EyeOff size={14} /> Hide instead</button>
          <button className="btn danger" data-testid="confirm-remove-field" onClick={() => { update((d) => ops.removeField(d, removing.id)); setSelected(null); setRemoving(null); }}><Trash2 size={14} /> Remove field</button>
        </>}>
          <div className="banner warning" role="alert"><div className="banner-body">
            <strong>This field holds data on {q.data.usage[removing.id]} asset{q.data.usage[removing.id] === 1 ? '' : 's'}</strong>
            <span>Removing it is destructive for everyday use: after publishing, its values leave every asset’s form, facets and search, and survive only as read-only archived values on each asset.</span>
          </div></div>
          <p className="muted" style={{ margin: 0 }}>To take the field out of sight but keep its values in place, hide it instead. A hidden field can be shown again at any time.</p>
        </Modal>
      )}
      {showHistory && (
        <Modal wide title="Published versions" onClose={() => setShowHistory(false)} actions={<button className="btn" onClick={() => setShowHistory(false)}>Close</button>}>
          {q.data.assetsWithArchivedValues > 0 && <div className="banner info">{q.data.assetsWithArchivedValues} asset{q.data.assetsWithArchivedValues > 1 ? 's hold' : ' holds'} archived values from earlier versions.</div>}
          {q.data.versions.map((v) => (
            <div className="run" key={v.version}>
              <div className="run-head"><strong>Version {v.version}</strong><span className="faint">{relative(v.publishedAt)}{v.createdBy ? ` · ${v.createdBy}` : ''}</span></div>
              {v.changes.length ? <ChangeList changes={v.changes} withImpact /> : <span className="muted">Initial model</span>}
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
}
